import React from 'react'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { FormikActions } from 'formik'
import * as Yup from 'yup'

import features from '../../config/feature-flags'

import './ScanPage.scss'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import { API } from '../../utils'
import { fetchOrderItemsByOrderNumber, OrderItem } from '../../utils/orderApi'
import { ScanFormValues, ScanOrderForm } from './ScanOrderForm'
import { AddSerialForm } from './AddSerialForm'
import loadingBox from '../../assets/images/loading_box.gif'
import { NotificationType } from '../../components'
import { HasGroup, UserAttributes, UserGroup, withArticlesContext } from '../../contexts'
import { Article, ArticleMapping } from '../../types/DataTypes'

import { SetNotification } from '../../contexts/NotificationProvider'
import { withNotificationContext } from '../../contexts/withNotificationContext'
import OrderResults from './OrderResults'
import {
  BULK_SERIAL_NUMBER_PATTERN,
  isValidBulkSerialNumberFormat,
  isValidSerialNumberFormat,
  SERIAL_NUMBER_PATTERN,
} from '../Scan/utils/serialNumbers'
import { AbsoluteSpinner } from '@dw/helix/lib/components/spinner/index.react'

export interface ScannedProduct {
  SKU: string
  count: number
  orderNo: string
  scannedCount?: number
}

interface OrderTracing {
  ProductId: string
  OrderNo: string
}

interface OrderScansInfo {
  Count: number
  ScannedCount: number
  Items: OrderTracing[]
  LastEvaluatedKey?: { id: string }
}

export type ScannerListener = (callback: (scannedText: string) => any) => (event: KeyboardEvent) => void

interface Props {
  userAttributes: UserAttributes
  userData: CognitoUser
  hasGroup: HasGroup
  setNotification: SetNotification
  articles: ArticleMapping
  rawArticles: Article[]
  articlesAreLoading: boolean
  setArticles: (articles?: Article[]) => void
}

interface State {
  gettingOrders: boolean
  newItemNos: string[]
  traces?: ScannedProduct[]
  orderItems: OrderItem[]
  previousOrderNo: string
  serials: string[][]
  deleteMode: boolean
  sendResultSuccess?: boolean
  sentOutput: string
  currentArticleNo: string
}

const getFlatArray = <T extends any>(arrayToFlat: T[][]): T[] => {
  return arrayToFlat.reduce((acc, val) => acc.concat(val), [])
}

const extractMatchingSerials = (text: string) => {
  if (isValidSerialNumberFormat(text)) return text.match(SERIAL_NUMBER_PATTERN)
  if (isValidBulkSerialNumberFormat(text)) return text.match(BULK_SERIAL_NUMBER_PATTERN)
  return []
}

export const ORDER_NUMBER_ACCESS_GROUPS = [
  UserGroup.Admin,
  UserGroup.SuperAdmin,
  UserGroup.BrandProtection,
  UserGroup.Warehouse,
  UserGroup.WarehouseExtended,
  UserGroup.Franchise,
] // everybody who sees scan page except distributor

export const BATTERY_CHANGE_ACCESS_GROUPS = [
  UserGroup.BrandProtection,
  UserGroup.SuperAdmin,
  UserGroup.Admin,
  UserGroup.Warehouse,
  UserGroup.WarehouseExtended,
]

export const INVENTORY_CONTROL_ACCESS_GROUPS = [
  UserGroup.BrandProtection,
  UserGroup.SuperAdmin,
  UserGroup.Admin,
  UserGroup.Warehouse,
  UserGroup.WarehouseExtended,
]

export const SHIP_TO_ACCESS_GROUPS = [UserGroup.Distributor]

class ScanPageComponent extends React.Component<Props, State> {
  static readonly scanLimit: number = parseInt(process.env.REACT_APP_SCAN_LIMIT!, 10)

  state: State = {
    gettingOrders: false,
    newItemNos: [],
    orderItems: [],
    previousOrderNo: '',
    serials: [],
    deleteMode: false,
    sentOutput: '',
    currentArticleNo: '',
  }

  private serialValidationSchema = Yup.string()
    .required("Serial Number can't be empty")
    .matches(/^[\dA-Z]{4}[\d]{7}$/, 'Serial Number is not valid')
    .test(
      'is-duplicate',
      'Serial Number is already added',
      serialNumber => getFlatArray(this.state.serials).indexOf(serialNumber) === -1,
    )
    .test('quantity-overflow', 'You cannot scan more items than the order total (all articles)', () => {
      if (features.remove_scan_page_validation) return true

      const serialsPendingSubmission = getFlatArray(this.state.serials).length
      const sumOfMaximumScannedCountAllowed = this.state.traces
        ? this.state.traces.reduce((total, item) => {
            if (!item.scannedCount) return total

            const count = item.scannedCount > item.count ? item.count : item.scannedCount
            return total + count
          }, 0)
        : 0
      const totalItemsInOrder = this.state.orderItems.reduce((total, item) => total + item.quantity, 0)
      if (totalItemsInOrder === 0) return true // for when there is no order selected (e.g. franchise users cant specify one)
      if (serialsPendingSubmission + sumOfMaximumScannedCountAllowed < totalItemsInOrder) return true
      return false
    })

  private scanSerialsListener?: (event: KeyboardEvent) => void

  componentDidMount(): void {
    this.scanSerialsListener = this.getScanListener(this.scanSerials)
    document.addEventListener('keypress', this.scanSerialsListener)
    document.addEventListener('click', this.globalClickListener)
  }

  componentWillUnmount(): void {
    this.props.setNotification(null)
    if (this.scanSerialsListener) {
      document.removeEventListener('keypress', this.scanSerialsListener)
    }

    document.removeEventListener('click', this.globalClickListener)
  }

  render() {
    const { userAttributes, rawArticles, articlesAreLoading, setNotification, hasGroup } = this.props
    const {
      currentArticleNo,
      deleteMode,
      gettingOrders,
      orderItems,
      sendResultSuccess,
      sentOutput,
      serials,
      traces,
    } = this.state

    if (articlesAreLoading) return <AbsoluteSpinner />

    return (
      <div data-component="scanpage-component">
        <div className="row">
          <div className="col-md-6">
            <div className="row">
              <div className="col-md-12">
                <ScanOrderForm
                  handleSubmit={this.send}
                  getAllOrders={this.getAllOrders}
                  rawArticles={rawArticles}
                  userLocations={userAttributes.locations || []}
                  getScanListener={this.getScanListener}
                  serials={serials}
                  setNotification={setNotification}
                  hasGroup={hasGroup}
                  handleArticleNumberChange={this.updateArticleNo}
                />
              </div>
            </div>
            {sendResultSuccess !== undefined && (
              <div className="row animated fadeIn">
                <div className="col-md-12">
                  <div className="panel reference-box">
                    <div className="panel-heading">Results</div>
                    <div className="panel-body">
                      <div className="scanned-output">
                        {sendResultSuccess && <span className="glyphicon glyphicon-ok dw-sent-icon" />}
                        {!sendResultSuccess && <span className="glyphicon glyphicon-remove dw-sent-icon" />}
                        <div>{sentOutput}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {gettingOrders ? (
              <div className="row animated fadeIn">
                <div className="col-md-12 order-search">
                  <h3 className="sub-headlines">Searching for the OrderNo…</h3>
                  <img src={loadingBox} alt="loading" />
                </div>
              </div>
            ) : (
              traces &&
              (traces.length > 0 ? (
                <OrderResults
                  traces={traces}
                  currentArticleNo={currentArticleNo}
                  serials={serials}
                  orderItems={orderItems}
                />
              ) : (
                <div className="row animated fadeIn order-fetch-results">
                  <div className="col-md-12 order-search">
                    <h3 className="sub-headlines">No order info found</h3>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="col-md-6">
            <div className="row">
              <div className="col-md-12">
                <AddSerialForm
                  serials={serials}
                  serialValidationSchema={this.serialValidationSchema}
                  addSerial={this.addSerial}
                  removeSerialGroup={this.removeSerialsGroup}
                  removeSerial={this.removeSerial}
                  removeAllSerials={this.removeAllSerials}
                  deleteMode={deleteMode}
                  deleteModeToggle={this.deleteModeToggle}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  private addSerial = (serial: string): void => {
    if (!this.state.serials.length) {
      this.setState({ serials: [[]] })
    }

    this.setState(
      state => ({
        serials: state.serials.map((serialsGroup, index, serials) =>
          index !== serials.length - 1 ? serialsGroup : [...serialsGroup, serial],
        ),
      }),
      this.scanLimitCheck,
    )
  }

  private addSerialsGroup = (serialsGroup: string[]): void => {
    this.setState(
      state => ({
        serials: [...state.serials, [...serialsGroup]],
      }),
      this.scanLimitCheck,
    )
  }

  private scanLimitCheck = () => {
    if (getFlatArray(this.state.serials).length >= ScanPageComponent.scanLimit) {
      this.props.setNotification({
        text:
          'You have exceeded the max amount to scan! We can not guarantee that all serial nos sent will be received',
        type: NotificationType.ERROR,
      })
    }
  }

  private removeSerialsGroup = (groupToRemove: string[]): void => {
    this.setState(state => ({ serials: state.serials.filter(group => group !== groupToRemove) }))

    if (!this.state.serials.length) {
      this.setState({ deleteMode: false })
    }
  }

  private removeSerial = (serialToRemove: string): void => {
    this.setState(state => ({
      serials: state.serials
        .map(serialsGroup =>
          serialsGroup.indexOf(serialToRemove) === -1
            ? serialsGroup
            : serialsGroup.filter(serial => serial !== serialToRemove),
        )
        .filter(serialGroup => !!serialGroup.length),
    }))

    if (!this.state.serials.length) {
      this.setState({ deleteMode: false })
    }
  }

  private removeAllSerials = (): void => {
    this.setState({ serials: [], deleteMode: false })
  }

  private deleteModeToggle = (): void => {
    this.setState({ deleteMode: !this.state.deleteMode })
  }

  private globalClickListener = (event: MouseEvent) => {
    if ((event.target as HTMLElement).id !== 'scan-delete-btn') {
      this.setState({ deleteMode: false })
    }
  }

  private getScanListener: ScannerListener = callback => {
    let typed: string = ''
    let typingTimer: NodeJS.Timer = {} as NodeJS.Timer
    return (event: KeyboardEvent) => {
      // all the query codes are scanned with new line at the end - removing it here - cleaning in one place
      if (event.key === 'Enter') {
        event.preventDefault()
        return
      }

      typed += event.key
      clearTimeout(typingTimer)

      typingTimer = setTimeout(() => {
        callback(typed)
        typed = ''
      }, 300)
    }
  }

  private scanSerials = (scannedText: string) => {
    const serials = extractMatchingSerials(scannedText)
    if (!(serials && serials.length)) {
      return
    }

    if (this.state.deleteMode) {
      serials.forEach(this.removeSerial)
      return
    }

    // removing all not valid, already added and duplicates
    const filteredSerials = serials.filter(
      (serial, index, self) => this.serialValidationSchema.isValidSync(serial) && self.indexOf(serial) === index,
    )

    const uniqueFilteredSerials = [...new Set(filteredSerials)]

    if (uniqueFilteredSerials.length) {
      this.addSerialsGroup(uniqueFilteredSerials)
    }
  }

  private getAllOrders = async (orderNo: string): Promise<undefined> => {
    if (orderNo.length === 0) return
    if (orderNo === this.state.previousOrderNo) return
    if (this.props.hasGroup([UserGroup.Franchise])) return

    this.setState({ gettingOrders: true, traces: [], orderItems: [], previousOrderNo: orderNo.toUpperCase() })

    const orderItems: OrderItem[] = await fetchOrderItemsByOrderNumber(orderNo.toUpperCase())
    const scannableOrderItems = orderItems.filter(({ articleId }) => {
      const { scannable } = this.props.articles[articleId] || {}
      return scannable
    })
    const items = await this.fetchOrderTracings(orderNo)
    this.setState({ orderItems: scannableOrderItems })
    this.buildItems(items, scannableOrderItems)
  }

  private fetchOrderScansFromITS = (orderNo: string, lastKey: string = ''): Promise<OrderScansInfo> => {
    const queryStringParameters: { searchParam: string; lastKey?: string } = { searchParam: orderNo }
    if (lastKey) {
      queryStringParameters.lastKey = lastKey
    }
    return API.get('its', '/tracing/order', { queryStringParameters })
  }

  private fetchOrderTracings = async (orderNo: string, lastKey: string = ''): Promise<OrderTracing[]> => {
    try {
      const orderTracingData = await this.fetchOrderScansFromITS(orderNo.toUpperCase(), lastKey)

      if (orderTracingData.LastEvaluatedKey) {
        return [
          ...orderTracingData.Items,
          ...(await this.fetchOrderTracings(orderNo, orderTracingData.LastEvaluatedKey.id)),
        ]
      }
      return orderTracingData.Items
    } catch (e) {
      this.setState({ gettingOrders: false })
      this.props.setNotification({
        text: 'Error connecting to ITS search, please try again later',
        type: NotificationType.ERROR,
      })
    }
    return []
  }

  private buildItems = (items: OrderTracing[], orderItems: OrderItem[]): void => {
    const results: ScannedProduct[] = []

    if (orderItems.length) {
      results.push(
        ...orderItems
          .filter(orderItem => orderItem.quantity)
          .map(orderItem => {
            return {
              SKU: orderItem.articleId,
              scannedCount: items.filter(item => orderItem.articleId === item.ProductId).length,
              count: orderItem.quantity,
              orderNo: orderItem.orderNo,
            }
          }),
      )
    } else {
      results.push(
        ...Object.values(
          items.reduce((acc: any, orderInfo: OrderTracing) => {
            let count = 0
            if (acc[orderInfo.ProductId]) {
              count = acc[orderInfo.ProductId].count
            }
            acc[orderInfo.ProductId] = { SKU: orderInfo.ProductId, count: count + 1, orderNo: orderInfo.OrderNo }
            return acc
          }, {}) as ScannedProduct[],
        ),
      )
    }

    this.setState({ gettingOrders: false, traces: results })
  }

  private trimStringInput = (input: any) => (typeof input === 'string' ? input.trim() : input)

  private buildJSONforSend = (formValues: ScanFormValues): any[] => {
    return getFlatArray(this.state.serials).map((serial: string) => {
      const obj: any = {
        id: (Math.random() * Date.now()).toString(36).replace('.', ''),
        serialNo: this.trimStringInput(serial),
        TraceEvent: this.trimStringInput(formValues['trace-event']),
        ReasonCode: this.trimStringInput(formValues['reason-code']) || undefined,
        SpecifiedReasonCode: this.trimStringInput(formValues['specified-reason-code']) || undefined,
        OrderNo: this.props.hasGroup(ORDER_NUMBER_ACCESS_GROUPS) ? this.trimStringInput(formValues['order-no']) : 'XX',
        ProductId: this.trimStringInput(formValues['article-no']),
        TraceTime: this.trimStringInput(formValues.date),
        'Company Name': this.trimStringInput(this.props.userAttributes.companyName),
        CompanySupplyChainRole: this.props.userAttributes.roles
          ? this.trimStringInput(this.props.userAttributes.roles[0])
          : '',
        UserLocation: this.trimStringInput(formValues.locations),
        Created: new Date().toISOString(),
      }
      if (this.props.hasGroup(SHIP_TO_ACCESS_GROUPS)) {
        obj.ShipTo = formValues['ship-to']
      } else if (this.props.hasGroup([UserGroup.Admin, UserGroup.SuperAdmin, UserGroup.Franchise])) {
        obj.ShipTo = 'Customer'
      }
      return obj
    })
  }
  private updateTraces = (addedItems: any[]): ScannedProduct[] => {
    return this.state.traces!.map((trace: ScannedProduct) => {
      if (addedItems.findIndex((item: any) => item.ProductId === trace.SKU) !== -1) {
        trace.scannedCount = (trace.scannedCount || 0) + addedItems.length
      }
      return trace
    })
  }

  private send = (formValues: ScanFormValues, actions: FormikActions<ScanFormValues>): void => {
    this.setState({ sendResultSuccess: undefined, sentOutput: '' })

    // Build the JSON obj
    const rows = this.buildJSONforSend(formValues)

    API.post('its', '/serial-handling', { body: { rows } })
      .then((data: any) => {
        if (data.success) {
          const { traces } = this.state

          if (traces && traces.length > 0) this.setState({ traces: this.updateTraces(rows) })

          this.removeAllSerials()
          this.setState({
            sendResultSuccess: true,
            sentOutput: `You have successfully scanned ${rows.length} serial numbers!`,
          })
        } else {
          this.setState({ sendResultSuccess: false })
        }
      })
      .catch(() => {
        this.setState({ sendResultSuccess: false })
      })
      .finally(() => actions.setSubmitting(false))
  }
  private updateArticleNo = (newArticleNo: string) => {
    this.setState({ currentArticleNo: newArticleNo })
  }
}

let EnhancedScanPageComponent = null

EnhancedScanPageComponent = withNotificationContext(ScanPageComponent)
EnhancedScanPageComponent = withArticlesContext(EnhancedScanPageComponent)

export const ScanPage = EnhancedScanPageComponent
